import CommonCss from "nlib/common/common.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Tooltip } from "nlib/ui";
import { getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import ActivityEvents from "const/ActivityEvents";
import Constants from "const/Constants";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import Environment from "utils/Environment";
import IntegrationServices from "const/IntegrationServices";
import Money from "utils/Money";
import Platforms from "utils/Platforms";
import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import moment from "moment";

const { EDITABLE_ACTIVITY_PROPERTIES } = ActivityEvents;

const DEFAULT_MATCHED_INFO_TYPE = "Transaction";

const PREV_VALUE_PROP_NAMES = Object.fromEntries(
  EDITABLE_ACTIVITY_PROPERTIES.map(({ propName, prevPropName }) => [propName, prevPropName])
);

const normalizeType = (type) => {
  return type.slice(0, 1).toUpperCase() + type.slice(1).toLowerCase();
};

const LinkToExternalService = ({ href, children }) => {
  const { QBO_APP_URL, XERO_APP_URL } = Environment.getProcessEnvVars();

  const { extraData: { integrationService } = {} } = useSelector(getSelectedBusinessData);

  return (
    <a
      href={integrationService === IntegrationServices.QUICK_BOOKS.value
        ? `${QBO_APP_URL}${href}` : `${XERO_APP_URL}${href}`}
      target="_blank"
      rel="noopener noreferrer">
      {children}
    </a>
  );
};

const ExpandedListItem = (props) => {
  const {
    header,
    type,
    item = {},
    shownColumnNames = [],
    possibleColumnCount = 0,
    descriptionColumnShown,
    matchedInfoColumnShown,
    matchedInfoLinksDisabled
  } = props;

  const {
    originalName,
    expense,
    amount,
    date,
    description,
    reason,
    prevReason = reason ? "" : undefined,
    matchedInfo,
    ...restItem
  } = item;

  const { uiTexts } = useSelector(getTextsData);

  const [tooltipOpened, setTooltipOpened] = useState({});

  const descColStyle = useMemo(() => {
    const colSpan = 1 + possibleColumnCount - shownColumnNames.length;

    return { width: `${colSpan}px`, flex: colSpan };
  }, [possibleColumnCount, shownColumnNames]);

  const handleMouseEnter = useCallback(({ currentTarget: { dataset: { name } } }) => {
    setTooltipOpened((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleMouseLeave = useCallback(({ currentTarget: { dataset: { name } } }) => {
    setTooltipOpened((prev) => ({ ...prev, [name]: false }));
  }, []);

  if (header) {
    return (
      <div className={classNames(Css.expandedListItem, Css.header)}>
        <div className={Css.amount}>
          <div><div>{uiTexts.amount}</div></div>
        </div>
        <div className={Css.date}>
          <div><div>{uiTexts.date}</div></div>
        </div>
        {shownColumnNames.map((keys) => (
          <div key={keys[0]} name={keys[0]}>
            <div><div>{keys.map((key) => uiTexts[key]).join(" / ")}</div></div>
          </div>
        ))}
        {matchedInfoColumnShown && (
          <div style={descColStyle} className={Css.matchedInfo}>
            <div>{uiTexts.matchedWith}</div>
          </div>
        )}
        <div className={Css.description} style={descColStyle}>
          <div><div>{descriptionColumnShown && uiTexts.description}</div></div>
        </div>
      </div>
    );
  }
  if (type === ActivityEvents.TYPES.DOCUMENTS_AUTO_APPROVED) {
    const issueDate = item.issueDate ? moment.utc(item.issueDate).format(DateTimeFormats.DATE_TEXT) : "";

    return (
      <div className={Css.expandedListItem}>
        <div className={Css.amount}>
          <div>
            <div className={Css.label}>{uiTexts.amount}:</div>
            {item.amount
              ? (
                <div
                  title={item.amount}
                  className={classNames(CommonCss.coloredNumber, Css.fontWeight)}
                  data-negative={item.paymentType === DocumentPaymentTypes.BUY ? "" : undefined}>
                  {Money.toMoneyString(item.amount, item.currency)}
                </div>
              )
              : Constants.EMPTY_PLACEHOLDER}
          </div>
        </div>
        <div className={Css.date} data-empty={!date || undefined}>
          <div>
            <div className={Css.label}>{uiTexts.date}:</div>
            <div title={issueDate}>{issueDate || Constants.EMPTY_PLACEHOLDER}</div>
          </div>
        </div>
        <div
          className={Css.description}
          style={descColStyle}>
          <div>
            <div className={Css.label}>{uiTexts.description}:</div>
            <div title={description || ""}>
              {description || Constants.EMPTY_PLACEHOLDER}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (type === ActivityEvents.TYPES.DOCUMENT_RECOGNITION_FAILED) {
    return (
      <div className={Css.expandedListItem}>
        <div className={Css.description} title={originalName}>
          <div><div>{originalName}</div></div>
        </div>
      </div>
    );
  }

  const data = { reason, prevReason, ...restItem };

  const dateValue = date ? moment.utc(date).format(DateTimeFormats.DATE_TEXT) : null;

  return (
    <div className={Css.expandedListItem}>
      <div className={Css.amount}>
        <div>
          <div className={Css.label}>{uiTexts.amount}:</div>
          <div
            title={amount || ""}
            className={classNames(CommonCss.coloredNumber, Css.fontWeight)}
            data-negative={expense ? "" : undefined}>{amount || Constants.EMPTY_PLACEHOLDER}</div>
        </div>
      </div>
      <div className={Css.date} data-empty={!date || undefined}>
        <div>
          <div className={Css.label}>{uiTexts.date}:</div>
          <div title={dateValue || ""}>{dateValue || Constants.EMPTY_PLACEHOLDER}</div>
        </div>
      </div>
      {shownColumnNames.map((keys) => {
        const [keyA, keyB, keyC] = keys;

        const key = data[keyC] ? keyC : (data[keyB] ? keyB : keyA);

        const value = data[key];

        if (!key) return null;

        const prevValue = data[PREV_VALUE_PROP_NAMES[key]];

        const icon = prevValue === "" && value
          ? <Icons.Plus weight="bold" className={classNames(Css.icon, Css.plus)} />
          : (!!prevValue && (
            value === ""
              ? <Icons.X weight="bold" className={classNames(Css.icon, Css.minus)} />
              : <Icons.ArrowsClockwise weight="bold" className={Css.icon} />
          ));

        return (
          <div
            key={key}
            name={key}
            data-name={key}
            data-empty={(!value && !prevValue) || undefined}
            onMouseEnter={Platforms.checkIsTouchDevice() ? undefined : handleMouseEnter}
            onMouseLeave={Platforms.checkIsTouchDevice() ? undefined : handleMouseLeave}>
            {icon || <span className={classNames(Css.icon, Css.empty)} />}
            {!Platforms.checkIsTouchDevice() && (prevValue || (prevValue === "" && value)) && (
              <Tooltip
                opened={tooltipOpened[key]}
                className={Css.tooltip}
                contentClassName={Css.tooltipContent}>
                {value ? <span><b>{prevValue}</b></span> : null}
                {prevValue && value ? <Icons.ArrowRight weight="bold" /> : null}
                {prevValue ? <span><b>{value}</b></span> : null}
                {prevValue === "" ? <b>{uiTexts.valueWasAdded}</b> : null}
                {value === "" ? <b>{uiTexts.valueWasCleared}</b> : null}
              </Tooltip>
            )}
            <div>
              <div className={Css.label}>
                {keys.map((keysItem) => uiTexts[keysItem]).join(" / ")}:
              </div>
              {icon}
              <div className={classNames(!value && Css.neutralColor)}>
                {prevValue || (prevValue === "")
                  ? (
                    !value && prevValue
                      ? <b title={prevValue}><s>{prevValue}</s></b>
                      : <b title={value || ""}>{value || Constants.EMPTY_PLACEHOLDER}</b>
                  )
                  : <span title={value || ""}>{value || Constants.EMPTY_PLACEHOLDER}</span>}
              </div>
            </div>
          </div>
        );
      })}
      {matchedInfoColumnShown && (
        <div style={descColStyle} className={Css.matchedInfo}>
          {!!matchedInfo && !!matchedInfo.type && (
            <div>
              {matchedInfo.href && !matchedInfoLinksDisabled ? (
                <LinkToExternalService href={matchedInfo.href}>
                  {matchedInfo.type}
                </LinkToExternalService>
              ) : (
                <span>{normalizeType(matchedInfo.type || DEFAULT_MATCHED_INFO_TYPE)}</span>
              )}
              {!!matchedInfo.amount && (
                <span>, {Money.toMoneyString(parseFloat(matchedInfo.amount))}</span>
              )}
              {!!matchedInfo.date && (
                <span>, {moment.utc(matchedInfo.date).format(DateTimeFormats.DATE_TEXT)}</span>
              )}
            </div>
          )}
        </div>
      )}
      <div
        className={Css.description}
        style={descColStyle}
        data-empty={!description || undefined}>
        <div>
          <div className={Css.label}>{uiTexts.description}:</div>
          <div
            title={(descriptionColumnShown && description) || ""}
            className={classNames(!description && Css.neutralColor)}>
            {descriptionColumnShown && (description || Constants.EMPTY_PLACEHOLDER)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ExpandedListItem);
